import type { LlmUIMessage, LlmUIMessageParts } from '@azymmetric/shared/types/llm.types';

type ProposeDataPart = Extract<LlmUIMessageParts, { type: 'tool-proposeData' }>;
type ProposeSuggestionPart = Extract<LlmUIMessageParts, { type: 'tool-proposeSuggestion' }>;

export type LlmChatComment = {
  id: string;
  /**
   * The highlighted excerpt of the draft this comment refers to.
   */
  quote: string;
  /**
   * The user's requested change for that excerpt.
   */
  text: string;
};

type LlmChatSuggestionBase = {
  /**
   * True when this is the pre-seeded, already-persisted content shown on open
   * (UPDATE mode) rather than a chat-produced draft. Never accept/reject-logged
   * and not acceptable — accepting the unchanged current value is a no-op.
   */
  isInitial?: boolean;
  /**
   * True while the tool-call input is still streaming in.
   */
  isStreaming: boolean;
  /**
   * LLM log id of the exchange that produced this version (accept/reject target).
   */
  logId?: string;
  /**
   * Model label for display (actual model when known, else the requested one).
   */
  model?: string;
  /**
   * Stable identity of this version (message id + part index).
   */
  versionId: string;
};

export type LlmChatSuggestion =
  | (LlmChatSuggestionBase & {
      /**
       * Plain-text draft for a single form field.
       */
      content: string;
      kind: 'text';
    })
  | (LlmChatSuggestionBase & {
      /**
       * Server-validated and resolved structured object, when the draft passed
       * validation.
       */
      data?: Record<string, unknown>;
      /**
       * Validation errors when the latest draft was rejected server-side.
       */
      errors?: string[];
      kind: 'data';
    });

const getTextPartContent = (part: ProposeSuggestionPart): string => {
  if (part.state === 'output-available') {
    return part.output.content;
  }

  if (part.state === 'input-available') {
    return part.input.content;
  }

  if (part.state === 'input-streaming') {
    const content = part.input?.content;
    return typeof content === 'string' ? content : '';
  }

  return '';
};

const getDataPartView = (
  part: ProposeDataPart,
): { data?: Record<string, unknown>; errors?: string[]; isStreaming: boolean } | undefined => {
  if (part.state === 'output-available') {
    return part.output.valid
      ? { data: part.output.data, isStreaming: false }
      : { errors: part.output.errors, isStreaming: false };
  }

  // The streaming input is the model's raw, unvalidated draft — its shape can
  // diverge from the validated object the server extraction stage produces, so
  // rendering it would flash a mismatched UI that the final artifact replaces.
  // Surface only the drafting state; the object renders once validated.
  if (part.state === 'input-available' || part.state === 'input-streaming') {
    return { isStreaming: true };
  }

  return undefined;
};

export const getMessageStreamMetadata = (
  message: LlmUIMessage,
): { actualModel?: string; logId?: string; requestedModel?: string } => {
  const metadata: { actualModel?: string; logId?: string; requestedModel?: string } = {};

  for (const part of message.parts) {
    if (part.type !== 'data-custom-metadata') continue;

    const { actualModel, logId, requestedModel } = part.data;
    if (actualModel) metadata.actualModel = actualModel;
    if (logId) metadata.logId = logId;
    if (requestedModel) metadata.requestedModel = requestedModel;
  }

  return metadata;
};

/**
 * Derive the latest Suggestion artifact version from the chat history: the most
 * recent proposeSuggestion (text) or proposeData (structured object) tool call
 * wins, and its exchange's stream metadata provides the log id + model used.
 *
 * An in-flight proposeData call (streaming args or validating server-side) never
 * replaces a completed artifact — the previous version stays visible, flagged as
 * streaming, until the new one lands. The bare drafting placeholder only shows
 * when no completed version exists yet, so repeated tool calls in one exchange
 * cannot flicker the panel between artifact and placeholder.
 */
export const deriveLatestSuggestion = (messages: LlmUIMessage[]): LlmChatSuggestion | undefined => {
  let pendingDraft: LlmChatSuggestion | undefined;

  for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex--) {
    const message = messages[messageIndex];
    if (message.role !== 'assistant') continue;

    for (let partIndex = message.parts.length - 1; partIndex >= 0; partIndex--) {
      const part = message.parts[partIndex];

      if (part.type === 'tool-proposeSuggestion') {
        const content = getTextPartContent(part);
        if (!content) continue;

        const { actualModel, logId, requestedModel } = getMessageStreamMetadata(message);

        return {
          content,
          isStreaming: part.state === 'input-streaming' || Boolean(pendingDraft),
          kind: 'text',
          logId,
          model: actualModel || requestedModel,
          versionId: `${message.id}:${partIndex}`,
        };
      }

      if (part.type === 'tool-proposeData') {
        const view = getDataPartView(part);
        if (!view) continue;

        const { actualModel, logId, requestedModel } = getMessageStreamMetadata(message);
        const version: LlmChatSuggestion = {
          ...view,
          kind: 'data',
          logId,
          model: actualModel || requestedModel,
          versionId: `${message.id}:${partIndex}`,
        };

        if (view.isStreaming && !view.data && !view.errors) {
          // In-flight draft: remember it and keep walking for the most recent
          // completed version to keep on screen meanwhile.
          pendingDraft = pendingDraft ?? version;
          continue;
        }

        return { ...version, isStreaming: view.isStreaming || Boolean(pendingDraft) };
      }
    }
  }

  return pendingDraft;
};

/**
 * Resolve the suggestion to display when an initial seed exists (UPDATE mode):
 * the seed renders until a chat draft supersedes it, and it stays visible —
 * flagged streaming — while the first draft is still a bare in-flight
 * placeholder, mirroring how completed versions behave during regeneration.
 */
export const resolveSuggestionWithSeed = (params: {
  derived: LlmChatSuggestion | undefined;
  seed: LlmChatSuggestion | undefined;
}): LlmChatSuggestion | undefined => {
  const { derived, seed } = params;

  if (!derived) return seed;

  const isBareDraftPlaceholder = derived.kind === 'data' && derived.isStreaming && !derived.data && !derived.errors;

  if (isBareDraftPlaceholder && seed) {
    return { ...seed, isStreaming: true };
  }

  return derived;
};

/**
 * Compose the outgoing chat message from staged comments + the typed text.
 * Comments become an explicit, model-friendly block referencing the excerpts.
 */
export const composeMessageWithComments = (params: { comments: LlmChatComment[]; text: string }): string => {
  const { comments, text } = params;
  const trimmedText = text.trim();

  if (comments.length === 0) {
    return trimmedText;
  }

  const commentsBlock = [
    'Suggested edits to specific parts of the current draft:',
    ...comments.map((comment) => `- "${comment.quote}"\n  Requested change: ${comment.text}`),
  ].join('\n');

  return trimmedText ? `${commentsBlock}\n\n${trimmedText}` : commentsBlock;
};
